import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { BranchOption, CityOption } from '@core/models/models';
import { groupBranches, orderBranchesBySubChain, subChainIcon } from './branch-groups';

@Component({
  selector: 'app-location-selector-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './location-selector-modal.component.html',
  styleUrl: './location-selector-modal.component.css'
})
export class LocationSelectorModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() cities: CityOption[] = [];
  @Input() selectedCity = '';
  @Input() citiesLoading = false;
  @Input() branches: BranchOption[] = [];
  @Input() selectedBranchId: number | null = null;
  @Input() branchesLoading = false;

  @Output() close = new EventEmitter<void>();
  @Output() cityChange = new EventEmitter<string>();
  @Output() branchChange = new EventEmitter<number | null>();

  citySearchQuery = '';
  filteredCities: CityOption[] = [];
  cityMenuOpen = false;
  activeCityIndex = -1;
  private readonly cityFilter$ = new Subject<string>();

  branchSearchQuery = '';
  filteredBranches: BranchOption[] = [];
  branchMenuOpen = false;
  activeBranchIndex = -1;
  private readonly branchFilter$ = new Subject<string>();

  private readonly destroy$ = new Subject<void>();
  private overlayEl: HTMLElement | null = null;
  private lockedScrollY: number | null = null;
  private releaseVisualPin: (() => void) | null = null;

  @ViewChild('sheetOverlay')
  set sheetOverlay(ref: ElementRef<HTMLElement> | undefined) {
    this.overlayEl = ref?.nativeElement ?? null;
    if (this.overlayEl) {
      this.bindVisualViewport();
    } else {
      this.releaseVisualViewport();
    }
  }

  get selectedCityLabel(): string {
    const city = this.cities.find((c) => c.value === this.selectedCity);
    return city?.label ?? this.selectedCity;
  }

  get selectedBranch(): BranchOption | null {
    return this.branches.find((b) => b.id === this.selectedBranchId) ?? null;
  }

  get selectedBranchLabel(): string {
    const branch = this.selectedBranch;
    return branch ? `${branch.chainName} · ${branch.branchName}` : '';
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (!this.isOpen) return;
    if (this.cityMenuOpen) {
      this.closeCityMenu();
      return;
    }
    if (this.branchMenuOpen) {
      this.closeBranchMenu();
      return;
    }
    this.closeModal();
  }

  @HostListener('document:click', ['$event'])
  onDocClick(event: MouseEvent): void {
    if (!this.isOpen) return;
    const target = event.target as HTMLElement | null;
    if (this.cityMenuOpen && target && !target.closest('.city-combobox-wrap')) {
      this.closeCityMenu();
    }
    if (this.branchMenuOpen && target && !target.closest('.branch-combobox-wrap')) {
      this.closeBranchMenu();
    }
  }

  ngOnInit(): void {
    this.cityFilter$
      .pipe(debounceTime(180), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((query) => {
        this.filterCities(query);
      });

    this.branchFilter$
      .pipe(debounceTime(180), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((query) => {
        this.filterBranches(query);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']) {
      if (this.isOpen) {
        this.syncInputsWithSelected();
        this.lockPageScroll();
      } else {
        this.unlockPageScroll();
      }
    }
    if (changes['cities'] && !this.cityMenuOpen) {
      this.filteredCities = this.cities;
    }
    if (changes['branches'] && !this.branchMenuOpen) {
      this.filteredBranches = orderBranchesBySubChain(this.branches);
    }
    if (changes['selectedCity'] && !changes['selectedCity'].firstChange) {
      this.citySearchQuery = this.selectedCityLabel;
    }
    if (changes['selectedBranchId'] && !changes['selectedBranchId'].firstChange) {
      this.branchSearchQuery = this.selectedBranchLabel;
    }
  }

  ngOnDestroy(): void {
    this.unlockPageScroll();
    this.releaseVisualViewport();
    this.destroy$.next();
    this.destroy$.complete();
  }

  syncInputsWithSelected(): void {
    this.citySearchQuery = this.selectedCityLabel;
    this.branchSearchQuery = this.selectedBranchLabel;
    this.filteredCities = this.cities;
    this.filteredBranches = orderBranchesBySubChain(this.branches);
    this.cityMenuOpen = false;
    this.branchMenuOpen = false;
    this.activeCityIndex = -1;
    this.activeBranchIndex = -1;
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closeModal();
    }
  }

  closeModal(): void {
    this.cityMenuOpen = false;
    this.branchMenuOpen = false;
    this.activeCityIndex = -1;
    this.activeBranchIndex = -1;
    this.close.emit();
  }

  filterCities(query: string): void {
    const q = (query || '').trim().toLowerCase();
    if (!q) {
      this.filteredCities = this.cities;
    } else {
      this.filteredCities = this.cities.filter(
        (c) => c.label.toLowerCase().includes(q) || c.value.toLowerCase().includes(q)
      );
    }
  }

  get branchGroups() {
    return groupBranches(this.branches);
  }

  iconFor(branch: BranchOption): string | null {
    return subChainIcon(branch);
  }

  filterBranches(query: string): void {
    const q = (query || '').trim().toLowerCase();
    const source = !q
      ? this.branches
      : this.branches.filter((b) => {
          const words = q.split(/\s+/).filter(Boolean);
          const full = `${b.chainName} ${b.subChainName || ''} ${b.branchName} ${b.address || ''} ${b.city || ''} ${b.externalCode || ''}`.toLowerCase();
          return words.every((w) => full.includes(w));
        });
    this.filteredBranches = orderBranchesBySubChain(source);
  }

  openCityMenu(): void {
    if (this.citiesLoading || this.cities.length === 0) return;
    this.cityMenuOpen = true;
    this.branchMenuOpen = false;
    const isCurrent = this.citySearchQuery.trim() === this.selectedCityLabel.trim();
    if (!this.citySearchQuery || isCurrent) {
      this.filteredCities = this.cities;
    } else {
      this.filterCities(this.citySearchQuery);
    }
    this.activeCityIndex = this.filteredCities.findIndex((c) => c.value === this.selectedCity);
    if (this.activeCityIndex >= 0) {
      this.scrollActiveOptionIntoView('city', this.activeCityIndex);
    }
    this.revealMenu('city-suggestions-list');
  }

  closeCityMenu(): void {
    this.cityMenuOpen = false;
    this.activeCityIndex = -1;
    const trimmed = (this.citySearchQuery || '').trim();
    if (trimmed && trimmed !== this.selectedCityLabel) {
      const exact = this.cities.find(
        (c) => c.label.toLowerCase() === trimmed.toLowerCase() || c.value.toLowerCase() === trimmed.toLowerCase()
      );
      if (exact) {
        this.selectCity(exact);
        return;
      }
    }
    this.citySearchQuery = this.selectedCityLabel;
  }

  toggleCityMenu(event: Event): void {
    event.stopPropagation();
    if (this.cityMenuOpen) {
      this.closeCityMenu();
    } else {
      this.openCityMenu();
      document.getElementById('shopping-city-input')?.focus();
    }
  }

  onCityInput(value: string): void {
    this.citySearchQuery = value;
    this.cityMenuOpen = true;
    this.activeCityIndex = -1;
    this.cityFilter$.next(value);
  }

  selectCity(city: CityOption): void {
    this.selectedCity = city.value;
    this.citySearchQuery = city.label;
    this.cityMenuOpen = false;
    this.activeCityIndex = -1;
    this.cityChange.emit(city.value);
    setTimeout(() => {
      document.getElementById('shopping-branch-input')?.focus();
    }, 60);
  }

  clearCitySelection(event?: Event): void {
    event?.stopPropagation();
    this.citySearchQuery = '';
    this.selectedCity = '';
    this.filteredCities = this.cities;
    this.activeCityIndex = -1;
    this.cityChange.emit('');
    this.cityMenuOpen = true;
    document.getElementById('shopping-city-input')?.focus();
  }

  onCityKeydown(event: KeyboardEvent): void {
    if (!this.cityMenuOpen) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.openCityMenu();
        return;
      }
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (this.filteredCities.length > 0) {
        this.activeCityIndex = (this.activeCityIndex + 1) % this.filteredCities.length;
        this.scrollActiveOptionIntoView('city', this.activeCityIndex);
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (this.filteredCities.length > 0) {
        this.activeCityIndex = (this.activeCityIndex - 1 + this.filteredCities.length) % this.filteredCities.length;
        this.scrollActiveOptionIntoView('city', this.activeCityIndex);
      }
    } else if (event.key === 'Enter') {
      if (this.cityMenuOpen && this.filteredCities.length > 0) {
        event.preventDefault();
        const chosen =
          this.activeCityIndex >= 0 && this.activeCityIndex < this.filteredCities.length
            ? this.filteredCities[this.activeCityIndex]
            : this.filteredCities[0];
        this.selectCity(chosen);
      }
    } else if (event.key === 'Escape') {
      if (this.cityMenuOpen) {
        event.preventDefault();
        event.stopPropagation();
        this.closeCityMenu();
      }
    }
  }

  openBranchMenu(): void {
    if (!this.selectedCity || this.branchesLoading || this.branches.length === 0) return;
    this.branchMenuOpen = true;
    this.cityMenuOpen = false;
    const isCurrent = this.branchSearchQuery.trim() === this.selectedBranchLabel.trim();
    if (!this.branchSearchQuery || isCurrent) {
      this.filteredBranches = orderBranchesBySubChain(this.branches);
    } else {
      this.filterBranches(this.branchSearchQuery);
    }
    this.activeBranchIndex = this.filteredBranches.findIndex((b) => b.id === this.selectedBranchId);
    if (this.activeBranchIndex >= 0) {
      this.scrollActiveOptionIntoView('branch', this.activeBranchIndex);
    }
    this.revealMenu('branch-suggestions-list');
  }

  closeBranchMenu(): void {
    this.branchMenuOpen = false;
    this.activeBranchIndex = -1;
    const trimmed = (this.branchSearchQuery || '').trim();
    if (trimmed && trimmed !== this.selectedBranchLabel) {
      const exact = this.branches.find(
        (b) =>
          `${b.chainName} · ${b.branchName}`.toLowerCase() === trimmed.toLowerCase() ||
          b.branchName.toLowerCase() === trimmed.toLowerCase()
      );
      if (exact) {
        this.selectBranch(exact);
        return;
      }
    }
    this.branchSearchQuery = this.selectedBranchLabel;
  }

  toggleBranchMenu(event: Event): void {
    event.stopPropagation();
    if (this.branchMenuOpen) {
      this.closeBranchMenu();
    } else {
      this.openBranchMenu();
      document.getElementById('shopping-branch-input')?.focus();
    }
  }

  onBranchInput(value: string): void {
    this.branchSearchQuery = value;
    this.branchMenuOpen = true;
    this.activeBranchIndex = -1;
    this.branchFilter$.next(value);
  }

  selectBranch(branch: BranchOption): void {
    this.selectedBranchId = branch.id;
    this.branchSearchQuery = `${branch.chainName} · ${branch.branchName}`;
    this.branchMenuOpen = false;
    this.activeBranchIndex = -1;
    this.branchChange.emit(branch.id);
  }

  clearBranchSelection(event?: Event): void {
    event?.stopPropagation();
    this.branchSearchQuery = '';
    this.selectedBranchId = null;
    this.filteredBranches = orderBranchesBySubChain(this.branches);
    this.activeBranchIndex = -1;
    this.branchChange.emit(null);
    this.branchMenuOpen = true;
    document.getElementById('shopping-branch-input')?.focus();
  }

  onBranchKeydown(event: KeyboardEvent): void {
    if (!this.branchMenuOpen) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.openBranchMenu();
        return;
      }
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (this.filteredBranches.length > 0) {
        this.activeBranchIndex = (this.activeBranchIndex + 1) % this.filteredBranches.length;
        this.scrollActiveOptionIntoView('branch', this.activeBranchIndex);
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (this.filteredBranches.length > 0) {
        this.activeBranchIndex =
          (this.activeBranchIndex - 1 + this.filteredBranches.length) % this.filteredBranches.length;
        this.scrollActiveOptionIntoView('branch', this.activeBranchIndex);
      }
    } else if (event.key === 'Enter') {
      if (this.branchMenuOpen && this.filteredBranches.length > 0) {
        event.preventDefault();
        const chosen =
          this.activeBranchIndex >= 0 && this.activeBranchIndex < this.filteredBranches.length
            ? this.filteredBranches[this.activeBranchIndex]
            : this.filteredBranches[0];
        this.selectBranch(chosen);
      }
    } else if (event.key === 'Escape') {
      if (this.branchMenuOpen) {
        event.preventDefault();
        event.stopPropagation();
        this.closeBranchMenu();
      }
    }
  }

  private scrollActiveOptionIntoView(type: 'city' | 'branch', index: number): void {
    requestAnimationFrame(() => {
      const option = document.getElementById(`${type}-opt-${index}`);
      const list = option?.parentElement;
      if (!option || !list) return;
      const top = option.offsetTop;
      const bottom = top + option.offsetHeight;
      if (top < list.scrollTop) {
        list.scrollTop = top;
      } else if (bottom > list.scrollTop + list.clientHeight) {
        list.scrollTop = bottom - list.clientHeight;
      }
    });
  }

  private lockPageScroll(): void {
    if (this.lockedScrollY != null) return;
    const scrollY = window.scrollY || document.documentElement.scrollTop || 0;
    this.lockedScrollY = scrollY;
    const body = document.body;
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
  }

  private unlockPageScroll(): void {
    if (this.lockedScrollY == null) return;
    const scrollY = this.lockedScrollY;
    this.lockedScrollY = null;
    const body = document.body;
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    const root = document.documentElement;
    const previousScrollBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    window.scrollTo(0, scrollY);
    root.style.scrollBehavior = previousScrollBehavior;
  }

  private bindVisualViewport(): void {
    if (this.releaseVisualPin) {
      this.pinOverlay();
      return;
    }
    const viewport = window.visualViewport;
    const media = window.matchMedia('(max-width: 640px)');
    const pin = () => this.pinOverlay();
    viewport?.addEventListener('resize', pin);
    viewport?.addEventListener('scroll', pin);
    media.addEventListener('change', pin);
    pin();
    this.releaseVisualPin = () => {
      viewport?.removeEventListener('resize', pin);
      viewport?.removeEventListener('scroll', pin);
      media.removeEventListener('change', pin);
      this.releaseVisualPin = null;
    };
  }

  private releaseVisualViewport(): void {
    this.releaseVisualPin?.();
    this.overlayEl = null;
  }

  private pinOverlay(): void {
    const overlay = this.overlayEl;
    if (!overlay) return;
    const phone = window.matchMedia('(max-width: 640px)').matches;
    const viewport = window.visualViewport;
    if (!phone || !viewport) {
      overlay.style.top = '';
      overlay.style.height = '';
      overlay.style.bottom = '';
      return;
    }
    overlay.style.top = `${Math.round(viewport.offsetTop)}px`;
    overlay.style.bottom = 'auto';
    overlay.style.height = `${Math.round(viewport.height)}px`;
    if (this.branchMenuOpen) this.revealMenu('branch-suggestions-list');
    else if (this.cityMenuOpen) this.revealMenu('city-suggestions-list');
  }

  private revealMenu(listId: string): void {
    requestAnimationFrame(() => {
      const list = document.getElementById(listId);
      const scroller = list?.closest('.settings-modal-body') as HTMLElement | null;
      const anchor = list?.previousElementSibling as HTMLElement | null;
      if (!list || !scroller || !anchor) return;
      const delta = anchor.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
      if (Math.abs(delta) > 1) scroller.scrollTop += delta;
    });
  }
}
